import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { adminQueryKeys, queryKeys } from '../lib/queryKeys';
import type { Team, Product } from '@sprintpulse/shared';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { TeamFormModal } from '../components/admin/TeamFormModal';
import { ProductFormModal } from '../components/admin/ProductFormModal';
import { ConfirmDialog } from '../components/admin/ConfirmDialog';

// Deterministic color from string — same string always gets the same hue
const PILL_COLORS = [
  { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },  // blue
  { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },  // green
  { bg: '#FDF4FF', text: '#7E22CE', border: '#E9D5FF' },  // purple
  { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },  // orange
  { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },  // red
  { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' },  // emerald
  { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE' },  // indigo
  { bg: '#FDF2F8', text: '#BE185D', border: '#FBCFE8' },  // pink
  { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' },  // amber
  { bg: '#F0FDFA', text: '#0F766E', border: '#99F6E4' },  // teal
];

function pillColor(s: string) {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  return PILL_COLORS[Math.abs(hash) % PILL_COLORS.length];
}

function Pill({ label }: { label: string }) {
  const c = pillColor(label);
  return (
    <span
      className="inline-block font-mono text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
    >
      {label}
    </span>
  );
}

function ActiveToggle({ team, onToggle, pending }: {
  team: Team; onToggle: () => void; pending: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={pending}
      title={team.isActive ? 'Click to deactivate' : 'Click to activate'}
      className="flex items-center gap-2 text-xs transition-opacity disabled:opacity-40"
    >
      <div
        className="w-7 h-4 rounded-full transition-colors relative"
        style={{ background: team.isActive ? 'var(--green)' : 'var(--border-mid)' }}
      >
        <div
          className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
          style={{ transform: team.isActive ? 'translateX(15px)' : 'translateX(2px)' }}
        />
      </div>
      <span style={{ color: team.isActive ? 'var(--green)' : 'var(--text-3)' }}>
        {team.isActive ? 'Active' : 'Inactive'}
      </span>
    </button>
  );
}

export function AdminPage(): JSX.Element {
  const qc = useQueryClient();

  // ── Products state ───────────────────────────────────────────────────────
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: adminQueryKeys.products(),
    queryFn: adminApi.listProducts,
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteProduct(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: adminQueryKeys.products() }); qc.invalidateQueries({ queryKey: queryKeys.products() }); setDeletingProduct(null); },
  });

  const closeProduct = (): void => { setShowProductForm(false); setEditingProduct(null); };

  // ── Teams state ──────────────────────────────────────────────────────────
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);

  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: adminQueryKeys.teams(),
    queryFn: adminApi.listTeams,
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleTeam(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: adminQueryKeys.teams() }); qc.invalidateQueries({ queryKey: queryKeys.teams() }); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteTeam(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: adminQueryKeys.teams() }); qc.invalidateQueries({ queryKey: queryKeys.teams() }); setDeletingTeam(null); },
  });

  const closeTeam = (): void => { setShowTeamForm(false); setEditingTeam(null); };

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-10">

      {/* ── Products Section ──────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
              Products
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-3)' }}>
              Products group teams and link to Jira roadmap projects.
            </p>
          </div>
          <button
            onClick={() => setShowProductForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-opacity hover:opacity-80"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <Plus size={14} /> Add product
          </button>
        </div>

        {productsLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-3)' }} />
          </div>
        ) : products.length === 0 ? (
          <div
            className="rounded-lg border-2 border-dashed py-12 text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--text-3)' }}>No products yet</p>
            <button
              onClick={() => setShowProductForm(true)}
              className="mt-3 text-xs underline"
              style={{ color: 'var(--accent)' }}
            >
              Add your first product
            </button>
          </div>
        ) : (
          <div
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-panel)' }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-widest"
                  style={{ background: 'var(--bg-subtle)', color: 'var(--text-3)' }}
                >
                  {['Product', 'Roadmap project', 'Column filter', 'Teams', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product, i) => {
                  const teamCount = teams.filter((t) => t.productId === product.id).length;
                  return (
                    <tr
                      key={product.id}
                      style={{ borderTop: i > 0 ? '1px solid var(--border)' : undefined }}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm" style={{ color: 'var(--text-1)' }}>
                          {product.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-2)' }}>
                        {product.jiraRoadmapProjectKey ? (
                          <span
                            className="px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                          >
                            {product.jiraRoadmapProjectKey}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-3)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-2)' }}>
                        <span
                          className="px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                        >
                          {product.roadmapColumnValue ?? 'Now'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-2)' }}>
                        {teamCount}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => { setEditingProduct(product); setShowProductForm(true); }}
                            className="p-1.5 rounded transition-opacity opacity-40 hover:opacity-100"
                            style={{ color: 'var(--text-2)' }}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => setDeletingProduct(product)}
                            className="p-1.5 rounded transition-opacity opacity-40 hover:opacity-100"
                            style={{ color: 'var(--red)' }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Teams Section ─────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
              Teams
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-3)' }}>
              Register and configure teams. Only active teams are collected nightly.
            </p>
          </div>
          <button
            onClick={() => setShowTeamForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-opacity hover:opacity-80"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <Plus size={14} /> Add team
          </button>
        </div>

        {/* Info banner */}
        <div
          className="rounded-lg p-3 mb-4 text-xs flex items-start gap-2"
          style={{ background: 'var(--accent-dim)', color: 'var(--text-2)', border: '1px solid var(--border)' }}
        >
          <span style={{ color: 'var(--accent)' }}>i</span>
          Teams are inactive by default. Toggle active to start nightly Jira collection.
        </div>

        {teamsLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-3)' }} />
          </div>
        ) : teams.length === 0 ? (
          <div
            className="rounded-lg border-2 border-dashed py-20 text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-sm" style={{ color: 'var(--text-3)' }}>No teams yet</p>
            <button
              onClick={() => setShowTeamForm(true)}
              className="mt-3 text-xs underline"
              style={{ color: 'var(--accent)' }}
            >
              Add your first team
            </button>
          </div>
        ) : (
          <div
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-panel)' }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-widest"
                  style={{ background: 'var(--bg-subtle)', color: 'var(--text-3)' }}
                >
                  {['Team', 'Product', 'Jira board', 'Project key(s)', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map((team, i) => (
                  <tr
                    key={team.id}
                    style={{
                      borderTop: i > 0 ? `1px solid var(--border)` : undefined,
                      opacity: team.isActive ? 1 : 0.6,
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm" style={{ color: 'var(--text-1)' }}>{team.name}</div>
                      {team.valueStream && (
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                          {team.valueStream}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {team.productName
                        ? <Pill label={team.productName} />
                        : <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-2)' }}>
                      {team.jiraBoardId}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {team.jiraProjectKey.split(',').map((k) => k.trim()).filter(Boolean).map((key) => (
                          <Pill key={key} label={key} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ActiveToggle
                        team={team}
                        onToggle={() => toggleMutation.mutate(team.id)}
                        pending={toggleMutation.isPending}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => { setEditingTeam(team); setShowTeamForm(true); }}
                          className="p-1.5 rounded transition-opacity opacity-40 hover:opacity-100"
                          style={{ color: 'var(--text-2)' }}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => setDeletingTeam(team)}
                          className="p-1.5 rounded transition-opacity opacity-40 hover:opacity-100"
                          style={{ color: 'var(--red)' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {showProductForm && (
        <ProductFormModal
          product={editingProduct}
          onClose={closeProduct}
          onSaved={() => { qc.invalidateQueries({ queryKey: adminQueryKeys.products() }); qc.invalidateQueries({ queryKey: queryKeys.products() }); closeProduct(); }}
        />
      )}

      {deletingProduct && (
        <ConfirmDialog
          title="Delete product?"
          message={`Permanently removes "${deletingProduct.name}". Products with linked teams cannot be deleted — unlink teams first.`}
          confirmLabel="Delete"
          isDestructive
          isLoading={deleteProductMutation.isPending}
          error={deleteProductMutation.error?.message}
          onConfirm={() => deleteProductMutation.mutate(deletingProduct.id)}
          onCancel={() => setDeletingProduct(null)}
        />
      )}

      {showTeamForm && (
        <TeamFormModal
          team={editingTeam}
          onClose={closeTeam}
          onSaved={() => { qc.invalidateQueries({ queryKey: adminQueryKeys.teams() }); qc.invalidateQueries({ queryKey: queryKeys.teams() }); closeTeam(); }}
        />
      )}

      {deletingTeam && (
        <ConfirmDialog
          title="Delete team?"
          message={`Permanently removes "${deletingTeam.name}" and all its sprint data. This cannot be undone.`}
          confirmLabel="Delete"
          isDestructive
          isLoading={deleteMutation.isPending}
          error={deleteMutation.error?.message}
          onConfirm={() => deleteMutation.mutate(deletingTeam.id)}
          onCancel={() => setDeletingTeam(null)}
        />
      )}
    </div>
  );
}
